import { Injectable, inject } from '@angular/core';
import { AirportCatalogService, normalizeIata } from 'src/app/core/services/airport-catalog/airport-catalog.service';

const LATAM_DESTINATION_IMAGES = 'https://s.latamairlines.com/images/destinations';

/**
 * Photo of one airport: https://s.latamairlines.com/images/destinations/{IATA}.jpg
 *
 * Callers pass the final destination. The URL is only that IATA, in uppercase.
 * Image failures belong to the card that attempted the load. The resolver intentionally never
 * blacklists a URL: a transient browser/CDN failure in one card must not suppress a valid image in
 * another card or in a later render.
 * prepare() still warms the airport catalog used for city names; the image URL does not use it.
 */
@Injectable({ providedIn: 'root' })
export class DestinationImageResolver {
  private readonly catalog = inject(AirportCatalogService);
  private readonly confirming = new Set<string>();

  /** Requests the airport catalog used for city names (no-op after the first call). */
  prepare(): void {
    this.catalog.load();
  }

  resolve(iata: string | null | undefined): string | null {
    const code = normalizeIata(iata);
    if (!code) return null;

    return `${LATAM_DESTINATION_IMAGES}/${code}.jpg`;
  }

  /**
   * Confirms a load error once for diagnostics without mutating resolution state. Every consumer
   * owns its retry/fallback state, so this result can never contaminate another card.
   */
  reportLoadError(url: string | null | undefined): void {
    if (!url || this.confirming.has(url)) return;

    this.confirming.add(url);
    this.probe(url).finally(() => {
      this.confirming.delete(url);
    });
  }

  /** Loads the image away from any ticket; resolves false only when that request fails too. */
  protected probe(url: string): Promise<boolean> {
    if (typeof Image === 'undefined') return Promise.resolve(false);

    return new Promise(resolve => {
      const image = new Image();
      image.referrerPolicy = 'no-referrer';
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = url;
    });
  }
}
